import React from "react";
import styles from "./button.module.scss";
import cn from "classnames";
import { ButtonBaseProps, ButtonSize } from "./button.typer";
import { track } from "../../api/tracking";
import useDebug from "../../hooks/useDebug";
import { v4 } from "uuid";

type ButtonProps = {
  secondaryText?: string;
  openClosedIcon?: boolean;
  tooltip?: string;
} & ButtonBaseProps &
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  >;

export default function Button({
  buttonType = "primary",
  size = "large",
  text,
  icon,
  setIconLast = false,
  style,
  type = "button",
  className,
  iconModifierClass,
  textModifierClass,
  alignButtonLeft = false,
  onClick,
  trackingName,
  secondaryText,
  openClosedIcon,
  tooltip,
  ...rest
}: ButtonProps) {
  const order = setIconLast ? styles.flipIconAndText : "";
  const iconOnly = text ? "" : styles.iconOnly;
  const iconSize = ButtonSize[size] === ButtonSize.small ? "sm" : "md";
  const debug = useDebug();

  const displayedIcon = icon && (
    <sds-icon
      className={iconModifierClass}
      size={iconSize}
      name={`${icon}-material-outlined`}
    />
  );
  const displayedSecondaryIcon = openClosedIcon !== undefined && (
    <sds-icon
      className={iconModifierClass}
      size={iconSize}
      name={
        openClosedIcon
          ? "keyboard_arrow_up-material-outlined"
          : "keyboard_arrow_down-material-outlined"
      }
    />
  );
  const displayedText = iconOnly ? (
    ""
  ) : (
    <span className={textModifierClass}>{text}</span>
  );

  const trackClickAndHandle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      track.userAction("Click button", trackingName);
      onClick(e);
    }
  };

  const tooltipId = tooltip && v4();

  return (
    <>
      <button
        className={cn(
          styles.iconLinkStructure,
          styles.iconLink,
          styles[buttonType],
          styles[size],
          styles.noTextWrap,
          order,
          className,
          iconOnly,
          alignButtonLeft ? styles.alignButtonLeft : "",
          style ? style : "",
          debug ? styles.debugTooltip : "",
          secondaryText && styles.column,
        )}
        type={type}
        onClick={trackClickAndHandle}
        data-tracking-name={trackingName}
        aria-describedby={tooltipId}
        {...rest}
      >
        <span className={cn(styles.primaryText, order)}>
          {displayedIcon}
          {displayedText}
          {displayedSecondaryIcon}
        </span>
        <span className={styles.secondaryText}>{secondaryText}</span>
      </button>
      {tooltip && tooltipId && (
        <sds-tooltip id={tooltipId}>{tooltip}</sds-tooltip>
      )}
    </>
  );
}
